import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { App } from 'antd';
import salesOrderService, {
  SalesOrder,
  SalesOrderRequest,
  SalesOrderFilters,
  AdvanceEligibilityResponse,
} from '@/services/salesOrder.service';

/**
 * Hook to fetch all sales orders
 */
export const useSalesOrders = (
  filters?: SalesOrderFilters,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['sales-orders', filters],
    queryFn: () => salesOrderService.getAll(filters),
    enabled: options?.enabled ?? true,
    staleTime: 0, // Always refetch to ensure fresh data
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });
};

/**
 * Hook to fetch a single sales order by ID
 */
export const useSalesOrder = (id: string | undefined) => {
  return useQuery({
    queryKey: ['sales-orders', id],
    queryFn: () => salesOrderService.getById(id!),
    enabled: !!id,
    staleTime: 30000,
  });
};

/**
 * Hook to fetch advance invoice eligibility for a sales order
 */
export const useAdvanceEligibility = (salesOrderId: string | undefined): UseQueryResult<AdvanceEligibilityResponse, Error> => {
  return useQuery({
    queryKey: ['sales-orders', salesOrderId, 'advance-eligibility'],
    queryFn: () => salesOrderService.getAdvanceEligibility(salesOrderId!),
    enabled: !!salesOrderId,
  });
};

export const useOrderStatusCheck = (salesOrderId: string | undefined) => {
  return useQuery({
    queryKey: ['sales-orders', salesOrderId, 'status-check'],
    queryFn: () => salesOrderService.getOrderStatusCheck(salesOrderId!),
    enabled: !!salesOrderId,
    staleTime: 30000,
  });
};

/**
 * Hook to create a new sales order
 */
export const useCreateSalesOrder = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data: SalesOrderRequest) => salesOrderService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      message.success('Customer Order created successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to create Customer Order');
    },
  });
};

/**
 * Hook to update a sales order
 */
export const useUpdateSalesOrder = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SalesOrderRequest }) =>
      salesOrderService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', variables.id, 'status-check'] });
      message.success('Customer Order updated successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update Customer Order');
    },
  });
};

/**
 * Hook to delete a sales order
 */
export const useDeleteSalesOrder = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesOrderService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      message.success('Customer Order deleted successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete Customer Order');
    },
  });
};

/**
 * Hook to update sales order status
 */
export const useUpdateSalesOrderStatus = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      salesOrderService.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', variables.id, 'status-check'] });
      message.success('Customer Order status updated successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update status');
    },
  });
};

/**
 * Hook to approve a sales order
 */
export const useApproveSalesOrder = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesOrderService.approve(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', id] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', id, 'status-check'] });
      message.success('Customer Order approved successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to approve Customer Order');
    },
  });
};

/**
 * Hook to cancel a sales order
 */
export const useCancelSalesOrder = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => salesOrderService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', id] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', id, 'status-check'] });
      message.success('Customer Order cancelled successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to cancel Customer Order');
    },
  });
};
